import React from 'react';
import { ref, onValue, set, push, serverTimestamp } from 'firebase/database';

// What the TOGΞ Dashboard and the Mod Dashboard share: talking to the bot, and the look of their pages.
// The pages don't decide who's allowed: every request goes to the bot (the dashboard cog) through dashboardInbox with
// the Discord login, the bot asks Discord whose login it is and checks they're allowed (bot owner, or the mod role for
// the Mod Dashboard), and the answer comes back in dashboardOutbox/<request>.

export const ERRORS = {
  login: 'Your Discord login has run out. Log in again to use the dashboard.',
  forbidden: "This Discord account can't use this dashboard.",
  timeout: "The bot didn't answer. Is it running, with the dashboard cog loaded?",
};

// One request to the bot; resolves with { ok, data } or { ok: false, error }
export const makeAsk = (database, getToken) => (action, args = {}, waitMs = 30000) => new Promise((resolve) => {
  const token = getToken();
  if (!token) { resolve({ ok: false, error: 'login' }); return; }
  const request = push(ref(database, 'dashboardInbox'));
  let off = () => {};
  let timer;
  const finish = (answer) => { off(); clearTimeout(timer); resolve(answer); };
  timer = setTimeout(() => finish({ ok: false, error: 'timeout' }), waitMs);
  off = onValue(ref(database, `dashboardOutbox/${request.key}`), (snapshot) => {
    const answer = snapshot.val();
    if (answer) finish(answer);
  }, () => finish({ ok: false, error: 'timeout' }));
  set(request, { token, action, args, at: serverTimestamp() }).catch(() => finish({ ok: false, error: 'timeout' }));
});

// Firebase drops empty lists from the bot's answers (and can turn a list into an object with "0", "1"… keys), so
// every list the page reads goes through this: a missing list is an empty one
export const asList = (value) => (Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : []);

// Around each tab: if one ever goes wrong, it says so in the tab instead of the whole page going blank
export class TabBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Dashboard tab crashed:', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <section className="rounded-xl border border-red-700/50 bg-red-900/20 p-4 text-xs text-red-200 space-y-2">
        <p className="font-semibold">This tab hit a problem and couldn't be shown.</p>
        <p className="font-mono text-[11px] text-red-300/80 break-words">{String(this.state.error?.message || this.state.error)}</p>
        <button type="button" onClick={() => this.setState({ error: null })}
          className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-white font-semibold">Try again</button>
      </section>
    );
  }
}

// The bot's replies are written for Discord: turn its markup into plain text for the page
export const plain = (text, channels = [], roles = []) => String(text || '')
  .replace(/<#(\d+)>/g, (_, id) => `#${channels.find(c => c.id === id)?.name || 'channel'}`)
  .replace(/<@&(\d+)>/g, (_, id) => `@${roles.find(r => r.id === id)?.name || 'role'}`)
  .replace(/<@!?(\d+)>/g, '@someone')
  .replace(/<t:(\d+):[a-zA-Z]>/g, (_, s) => new Date(s * 1000).toLocaleTimeString())
  .replace(/<a?:\w+:\d+>/g, '')
  .replace(/\*\*|`|\\(?=[_*~|])/g, '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

// 2d 4h · 3h 12m · 7m
export const duration = (ms) => {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(minutes / 1440), h = Math.floor((minutes % 1440) / 60), m = minutes % 60;
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};
export const since = (ms) => duration(Date.now() - ms);
// "in 2d 4h" / "5m ago"
export const relative = (ms) => (ms > Date.now() ? `in ${duration(ms - Date.now())}` : `${duration(Date.now() - ms)} ago`);

export const Card = ({ title, children, className = '', right }) => (
  <section className={`rounded-xl border border-zinc-800 bg-zinc-900/80 p-3 sm:p-4 ${className}`}>
    {(title || right) && (
      <div className="flex items-center justify-between gap-2 mb-3">
        {title && <h2 className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">{title}</h2>}
        {right}
      </div>
    )}
    {children}
  </section>
);

export const Button = ({ children, onClick, disabled, tone = 'plain', small, type = 'button' }) => {
  const tones = {
    plain: 'bg-zinc-800 hover:bg-zinc-700 text-white',
    primary: 'bg-[#5865f2] hover:bg-[#4752c4] text-white',
    success: 'bg-[#23a55a] hover:bg-[#1a8a4a] text-white',
    danger: 'bg-red-600/90 hover:bg-red-600 text-white',
    ghost: 'text-zinc-300 hover:text-white hover:bg-zinc-800',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={`${tones[tone]} ${small ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'} rounded-md font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap`}>
      {children}
    </button>
  );
};

// The green / red line under the tabs saying how the last action went
export const Notice = ({ note }) => note ? (
  <div className={`mb-4 rounded-lg px-3 py-2 text-xs border whitespace-pre-line ${note.ok ? 'border-green-700/50 bg-green-900/20 text-green-300' : 'border-red-700/50 bg-red-900/20 text-red-300'}`}>
    {note.text}
  </div>
) : null;

// Not logged in / not allowed
export const Gate = ({ title, text, onLogin }) => (
  <main className="flex-1">
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <p className="text-4xl mb-3">🔒</p>
      <h1 className="text-white text-lg font-bold mb-1">{title}</h1>
      <p className="text-zinc-400 text-sm mb-5">{text}</p>
      {onLogin && <Button tone="primary" onClick={onLogin}>Log in with Discord</Button>}
    </div>
  </main>
);

export const inputClass = 'w-full min-w-0 rounded-md bg-zinc-950 border border-zinc-700 px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500 disabled:opacity-40';
export const selectClass = 'rounded-md bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-zinc-500 disabled:opacity-40';

// The server's text channels, grouped by category, as <option>s for a channel dropdown
export const channelOptions = (channels) => Object.entries(channels.reduce((groups, c) => {
  (groups[c.category || 'No category'] = groups[c.category || 'No category'] || []).push(c);
  return groups;
}, {})).map(([category, list]) => (
  <optgroup key={category} label={category}>
    {list.map(c => <option key={c.id} value={c.id} disabled={!c.canPost}>#{c.name}{c.canPost ? '' : ' (TOGΞ can’t post here)'}</option>)}
  </optgroup>
));

// A small round picture, or the first letter if there isn't one
export const Avatar = ({ src, name, size = 24 }) => src
  ? <img src={src} alt="" className="rounded-full flex-shrink-0 object-cover" style={{ width: size, height: size }} />
  : <span className="rounded-full flex-shrink-0 bg-zinc-700 text-white font-bold flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.45 }}>{(name || '?').slice(0, 1).toUpperCase()}</span>;
